<template>
  <div class="grid-wrap">
    <div
      class="grid"
      :style="gridStyle"
      @pointerleave="hover = null"
    >
      <div />
      <div class="stage" :style="{ gridColumn: `span ${section.columns}` }">
        PALCO
      </div>
      <div />

      <template v-for="row in section.rows" :key="row.id">
        <button
          type="button"
          class="row-symbol"
          :title="`Selecionar a fila ${row.symbol}`"
          @click="selection.selectRow(row.id, $event.ctrlKey || $event.metaKey)"
        >
          {{ row.symbol }}
        </button>

        <div
          v-for="col in cols"
          :key="`${row.id}-${col}`"
          class="cell"
          :class="cellClass(row.id, col)"
          :style="cellStyle(row.id, col)"
          @pointerdown="onDown($event, row.id, col)"
          @pointerenter="onEnter(row.id, col)"
          @click="onClick($event, row.id, col)"
        >
          <template v-if="seatAt(row.id, col)">
            <span class="cell__name">{{ seatAt(row.id, col)!.name }}</span>
            <i
              v-if="SeatStatusIcons[seatAt(row.id, col)!.status]"
              :class="[SeatStatusIcons[seatAt(row.id, col)!.status], 'cell__status']"
            />
            <span v-if="seatAt(row.id, col)!.type !== SeatType.STANDARD" class="cell__type" />
          </template>
        </div>

        <button
          type="button"
          class="row-symbol"
          :title="`Selecionar a fila ${row.symbol}`"
          @click="selection.selectRow(row.id, $event.ctrlKey || $event.metaKey)"
        >
          {{ row.symbol }}
        </button>
      </template>
    </div>
  </div>

  <div class="hover-info text-body-2 mt-3">
    {{ hoverInfo }}
  </div>

  <div class="legend d-flex flex-wrap mt-2">
    <span v-for="status in statuses" :key="status" class="legend__item">
      <seat-status-swatch :status="status" />
      {{ SeatStatusLabels[status] }}
    </span>
    <span class="legend__item">
      <span class="legend__type"><span class="cell__type" /></span>
      Cadeira de rodas, pessoa obesa ou mobilidade reduzida
    </span>
  </div>
</template>

<script lang="ts" setup>
// The open Section's grid on the seat blocking page (IDV-705): the stage at the top, Rows front to back with their
// symbols on both sides, each seat painted by its status. Clicks, drags and Row symbols go to `selection`.
import { computed, onBeforeUnmount, onMounted, ref, type CSSProperties, type Ref } from 'vue'
import { SeatStatus, SeatStatusColors, SeatStatusHints, SeatStatusIcons, SeatStatusLabels, isChangeable } from '@/constants/SeatStatuses'
import { SeatType, SeatTypeLabels } from '@/constants/SeatTypes'
import type { EventSeat } from '@/types/EventSeats'
import type { BoardSection, Notice } from '@/helpers/seatBlocking/board'
import type { CellRef, SeatSelection } from '@/helpers/seatBlocking/selection'
import SeatStatusSwatch from './seat-status-swatch.vue'

interface Props {
  section: BoardSection
  selection: SeatSelection
  /** Seats that just changed elsewhere, flashed for a moment */
  flashing: Set<string>
}

const { section, selection, flashing } = defineProps<Props>()

const emit = defineEmits<{
  notice: [notice: Notice]
}>()

defineOptions({ name: 'SeatBlockingGrid' })

const statuses: SeatStatus[] = Object.values(SeatStatus)

const hover: Ref<CellRef | null> = ref(null)
/** Set when a box drag ends, so the click that follows the release doesn't replace the selection */
let suppressClick = false

const cols = computed<number[]>(() => Array.from({ length: section.columns }, (_, i) => i + 1))

const gridStyle = computed<Record<string, string>>(() => ({
  gridTemplateColumns: `36px repeat(${section.columns}, 30px) 36px`,
}))

/** The seats of each Row by column */
const seatMap = computed<Map<string, Map<number, EventSeat>>>(() => new Map(
  section.rows.map(row => [row.id, new Map(row.seats.map(seat => [seat.position, seat]))]),
))

function seatAt(rowId: string, col: number): EventSeat | undefined {
  return seatMap.value.get(rowId)?.get(col)
}

function cellClass(rowId: string, col: number): Record<string, boolean> {
  const seat = seatAt(rowId, col)
  if (!seat) return { 'cell--gap': true }

  return {
    'cell--seat': true,
    'cell--locked': !isChangeable(seat.status),
    'cell--selected': selection.has(seat.id),
    'cell--box': selection.inBox({ rowId, col }),
    'cell--hover': hover.value?.rowId === rowId && hover.value.col === col,
    'cell--flash': flashing.has(seat.id),
  }
}

function cellStyle(rowId: string, col: number): CSSProperties | undefined {
  const seat = seatAt(rowId, col)
  if (!seat) return undefined

  const colors = SeatStatusColors[seat.status]
  return { background: colors.background, borderColor: colors.border, color: colors.color }
}

const hoverInfo = computed<string>(() => {
  const h = hover.value
  const seat = h ? seatAt(h.rowId, h.col) : undefined
  if (!seat) return 'Passe o mouse sobre um lugar para ver o status.'

  return `${seat.full_label} · ${SeatTypeLabels[seat.type]} · ${SeatStatusLabels[seat.status]}: ${SeatStatusHints[seat.status]}`
})

function onDown(event: PointerEvent, rowId: string, col: number) {
  if (event.button !== 0) return
  suppressClick = false

  // Touch pointers are captured by the cell they start on; release so pointerenter fires on the next cells
  const target = event.currentTarget as HTMLElement
  if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)

  if (!event.shiftKey) selection.press({ rowId, col }, event.ctrlKey || event.metaKey)
}

function onEnter(rowId: string, col: number) {
  hover.value = { rowId, col }
  selection.drag({ rowId, col })
}

function onClick(event: MouseEvent, rowId: string, col: number) {
  if (suppressClick) {
    suppressClick = false
    return
  }

  const notice = selection.click({ rowId, col }, { shift: event.shiftKey, toggle: event.ctrlKey || event.metaKey })
  if (notice) emit('notice', notice)
}

function onPointerUp() {
  if (selection.release()) suppressClick = true
}

// A drag also ends when the browser takes the pointer or the window loses focus, or polling would wait for it forever
const DRAG_ENDS = ['pointerup', 'pointercancel', 'blur'] as const

onMounted(() => DRAG_ENDS.forEach(name => window.addEventListener(name, onPointerUp)))

onBeforeUnmount(() => DRAG_ENDS.forEach(name => window.removeEventListener(name, onPointerUp)))
</script>

<style scoped>
.grid-wrap {
  overflow: auto;
  padding: 8px 0 16px;
}

.grid {
  display: grid;
  gap: 3px;
  width: max-content;
  margin: 0 auto;
  user-select: none;
  touch-action: none;
}

.stage {
  height: 26px;
  margin-bottom: 10px;
  border-radius: 0 0 40px 40px;
  background: #37474f;
  color: #fff;
  font: 600 12px/26px sans-serif;
  letter-spacing: 0.4em;
  text-align: center;
}

.row-symbol {
  height: 30px;
  border-radius: 4px;
  font: 600 13px/30px sans-serif;
  color: #37474f;
  text-align: center;
  cursor: pointer;
}

.row-symbol:hover {
  background: rgba(0, 0, 0, 0.06);
}

.cell {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px 6px 3px 3px;
  font: 600 11px/1 ui-monospace, Menlo, monospace;
}

.cell--seat {
  border: 1.5px solid;
  cursor: pointer;
}

.cell--locked {
  cursor: not-allowed;
}

.cell--hover {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.45);
}

.cell--box:not(.cell--selected) {
  outline: 2px dashed rgba(var(--v-theme-primary), 0.6);
  outline-offset: 1px;
}

.cell--selected {
  outline: 2.5px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
  z-index: 1;
}

.cell--flash {
  animation: flash 0.8s ease-in-out 4;
}

@keyframes flash {
  50% { box-shadow: 0 0 0 4px #e53935; }
}

.cell__status {
  position: absolute;
  right: 2px;
  bottom: 2px;
  font-size: 7px;
}

.cell__type {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8e24aa;
}

.hover-info {
  min-height: 20px;
  color: rgba(0, 0, 0, 0.65);
}

.legend {
  gap: 4px 16px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

.legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend__type {
  position: relative;
  width: 18px;
  height: 18px;
  border: 1.5px solid #90a4ae;
  border-radius: 5px 5px 2px 2px;
}
</style>
